import { apiClient } from '.';

const BASE_URL = '/admin/v1/reservation-success';

export interface ReservationSuccessBatchRun {
  runId: string;
  status: 'running' | 'completed' | 'failed' | 'partial';
  invocationType: 'scheduled_hourly' | 'manual';
  hourBucket: string;
  startedAt: string;
  completedAt?: string;
  durationMs?: number;
  summary?: {
    partnerCount: number;
    sessionCount: number;
    productCount: number;
    gradeDistribution: Record<string, number>;
    snapshotsWritten: number;
    phaseDurationsMs?: Record<string, number>;
  };
  llmStats?: { success: number; fallback: number; fail: number };
  alertStats?: { sent: number; skipped: number; failed: number };
  error?: string;
  logs?: string[];
}

export interface ReservationSuccessMonitoringDashboard {
  healthy: boolean;
  lastSuccessfulRunAt: string | null;
  lastRunStatus: string;
  snapshotFreshness: { scheduleRisk: string | null; productDiagnosis: string | null };
  staleAlert: boolean;
  currentHourBucket: string;
  gradeDistribution: Record<string, number>;
  alerts: Array<{ level: 'error' | 'warn'; message: string }>;
}

export type ReservationGrade = 'recommended' | 'stable' | 'caution' | 'danger';
export type ApplicationStatus = 'active' | 'stagnant' | 'no_application';

export interface ReservationSuccessProductIndicatorRow {
  partnerId: string;
  partnerName: string;
  productId: string;
  productName?: string;
  bookings3d: number;
  applicationStatus: ApplicationStatus;
  cancelRate1m?: number;
  cancelRate2m?: number;
  cancelRate3m?: number;
  byGrade: Record<ReservationGrade, number>;
  /** Highest-severity active session in the selected date range */
  dDayStockLabel?: string;
  combinedDiagnosis?: string;
  representativeSessionDate?: string;
  lastUpdatedAt?: string;
}

export type ReservationSuccessProductIndicatorSortField =
  | 'partnerName'
  | 'productName'
  | 'bookings3d'
  | 'cancelRate1m'
  | 'cancelRate2m'
  | 'cancelRate3m'
  | 'danger'
  | 'caution'
  | 'recommended'
  | 'stable';

export interface ReservationSuccessProductIndicatorsQuery {
  keyword?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  size?: number;
  sortField?: ReservationSuccessProductIndicatorSortField;
  sortOrder?: 'asc' | 'desc';
}

export interface ReservationSuccessProductIndicatorsResponse {
  contents: ReservationSuccessProductIndicatorRow[];
  total: number;
  page: number;
  size: number;
  hourBucket: string;
  dateFrom: string;
  dateTo: string;
  summary: {
    partnerCount: number;
    productCount: number;
    byGrade: Record<ReservationGrade, number>;
  };
}

export const runReservationSuccessBatch = async () => {
  return apiClient.post('/admin/v1/schedule/reservation-success/batch/run', null, {
    params: { invocationType: 'manual' },
  });
};

export const listReservationSuccessBatchRuns = async (params?: { limit?: number; status?: string }) => {
  return apiClient.get<ReservationSuccessBatchRun[]>(`${BASE_URL}/batch/runs`, { params });
};

export const getReservationSuccessBatchRun = async (runId: string) => {
  return apiClient.get<ReservationSuccessBatchRun>(`${BASE_URL}/batch/runs/${runId}`);
};

export const getReservationSuccessMonitoringDashboard = async () => {
  return apiClient.get<ReservationSuccessMonitoringDashboard>(`${BASE_URL}/monitoring/dashboard`);
};

export interface ReservationSuccessAlertStatusCounts {
  total: number;
  pending: number;
  sent: number;
  skipped: number;
  failed: number;
}

export interface ReservationSuccessRiskSession {
  partnerId: string;
  partnerName: string;
  productId?: string;
  optionId?: string;
  productName?: string;
  optionName?: string;
  sessionDate: string;
  dDay?: number;
  reservationGrade?: ReservationGrade;
  combinedDiagnosis?: string;
  applicantCount?: number;
  worstShortRate?: number;
  lcEligible: boolean;
}

export interface ReservationSuccessOverview {
  hourBucket: string;
  range: { from: string; to: string; days: number };
  health: ReservationSuccessMonitoringDashboard;
  sessions: {
    totalSessions: number;
    preDiagnosisSessions: number;
    byGrade: Record<ReservationGrade, number>;
    lcEligibleOpen: number;
    lastChanceRegistered: number;
    unassignedEnrollments: number;
  };
  riskSessions: ReservationSuccessRiskSession[];
  alerts: {
    today: ReservationSuccessAlertStatusCounts;
    last7Days: ReservationSuccessAlertStatusCounts;
    byType7Days: Array<{ alertType: string; count: number }>;
  };
}

export const getReservationSuccessOverview = async (params?: { days?: number }) => {
  return apiClient.get<ReservationSuccessOverview>(`${BASE_URL}/monitoring/overview`, { params });
};

export interface ReservationSuccessAlertLog {
  _id: string;
  partnerId: string;
  partnerName: string;
  alertType: string;
  referenceId: string;
  alertDate: string;
  status: 'pending' | 'sent' | 'skipped' | 'failed';
  channel?: string;
  grade?: string;
  message?: string;
  error?: string;
  retryCount: number;
  createdAt?: string;
}

export interface ReservationSuccessAlertLogsQuery {
  dateFrom: string;
  dateTo: string;
  page?: number;
  size?: number;
  status?: string;
  alertType?: string;
  partnerId?: string;
}

export interface ReservationSuccessAlertLogsResponse {
  contents: ReservationSuccessAlertLog[];
  total: number;
  page: number;
  size: number;
  summary: ReservationSuccessAlertStatusCounts;
  alertTypes: Array<{ alertType: string; count: number }>;
}

export const listReservationSuccessAlertLogs = async (params: ReservationSuccessAlertLogsQuery) => {
  return apiClient.get<ReservationSuccessAlertLogsResponse>(`${BASE_URL}/monitoring/alerts`, { params });
};

export const listReservationSuccessProductIndicators = async (params?: ReservationSuccessProductIndicatorsQuery) => {
  return apiClient.get<ReservationSuccessProductIndicatorsResponse>(`${BASE_URL}/monitoring/product-indicators`, {
    params,
  });
};

export interface ClearMarketMetricProduct {
  productId: string;
  productName: string;
  sellerName: string;
  sessions: number;
  soldSessions: number;
  purchased: number;
  capacity: number;
}

export interface ReservationSuccessMetrics {
  from: string;
  to: string;
  dDay: number;
  generatedAt: string;
  backtest: {
    sessions: number;
    unlabeled: number;
    truePositive: number;
    falsePositive: number;
    falseNegative: number;
    trueNegative: number;
    accuracy: number | null;
    falsePositiveRate: number | null;
    falseNegativeRate: number | null;
    precision: number | null;
    recall: number | null;
    grades: Array<{ grade: string; sessions: number; fullRefundSessions: number; rate: number | null }>;
  };
  clearMarket: {
    products: number;
    partners: number;
    registeredSessions: number;
    cancelledSessions: number;
    liveSessions: number;
    soldSessions: number;
    purchased: number;
    capacity: number;
    conversion: { numerator: number; denominator: number; rate: number | null };
    seatFill: { numerator: number; denominator: number; rate: number | null };
    rows: ClearMarketMetricProduct[];
  };
  refund: {
    before: { from: string; to: string; paid: number; refundedAll: number; rate: number | null };
    after: { from: string; to: string; paid: number; refundedAll: number; rate: number | null };
  };
  recommendation: { clicks: number; keptPurchases: number; rate: number | null };
}

export const getReservationSuccessMetrics = async (params: { from: string; to: string; dDay: number }) => {
  return apiClient.get<ReservationSuccessMetrics>(`${BASE_URL}/metrics`, { params });
};

export const exportReservationSuccessSessionIndicatorsExcel = async (
  params?: Pick<ReservationSuccessProductIndicatorsQuery, 'keyword' | 'dateFrom' | 'dateTo'>,
) => {
  return apiClient.get(`${BASE_URL}/monitoring/product-indicators/sessions/export`, {
    params,
    responseType: 'blob',
    headers: { 'Cache-Control': 'no-cache' },
  });
};
